import {
  IExperienceTemplateResource,
  ISubTaskCalendlyInviteTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import {
  CalendlyCollection,
  CalendlyEventType
} from '@dadohr/shared/domainTypes/integrations/calendly';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { Button, FormHelperText, styled, Typography } from '@material-ui/core';
import React, { SetStateAction } from 'react';
import { WithShape } from '../../../../../../../../components/Charts/Util';
import { FormRowGroup } from '../../../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../../../components/Loader';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { SomethingWentWrong } from '../../../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../../services/currentUser';
import { useFirebaseFunction } from '../../../../../../../../services/firebaseFunctions';
import { ONE_MINUTE } from '../../../../../../../../services/time';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_CALENDLY_INVITE
} from '../../../validation';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

const toUrl = (fnName: string) => `integrations_calendly-${fnName}`;

const useEvents = () => {
  const { org } = useCurrentUser();
  return useFirebaseFunction<CalendlyCollection<CalendlyEventType>>(
    toUrl('listEvents'),
    { orgId: org.id },
    [org.id],
    ONE_MINUTE
  );
};

const EventContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(1)
}));

const EventBody = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const EventButton = styled(Button)((p) => ({
  '&&': {
    width: '100%',
    justifyContent: 'flex-start'
  }
}));

export const BlockSettingsSubTaskCalendlyInvite = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  resources,
  onChangeResources,
  goToSubPage,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskCalendlyInviteTemplate;
  onChange: (nextValue: ISubTaskCalendlyInviteTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];

  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const [events, loading, error] = useEvents();
  const validateEvent = useFieldValidator(
    setValidation,
    VALIDATORS_CALENDLY_INVITE,
    'event'
  );
  const invalidEventReason = validation?.fields.event || null;

  if (loading) {
    return <Loader height={150} />;
  }
  if (error) {
    return <SomethingWentWrong height={150} />;
  }
  if (!events) {
    // should never happen
    return null;
  }
  return (
    <>
      <FormRowGroup>
        {events.collection.map((ev) => (
          <EventContainer key={ev.uri}>
            <EventButton
              variant="outlined"
              color={value.data.event?.uri === ev.uri ? 'primary' : 'default'}
              onClick={() => {
                const nextValue = {
                  ...value,
                  data: {
                    ...value.data,
                    event: {
                      uri: ev.uri,
                      schedulingUrl: ev.scheduling_url,
                      name: ev.name,
                      color: ev.color
                    }
                  }
                };
                invalidEventReason && validateEvent(nextValue, expTpl);
                onChange(nextValue);
              }}
            >
              <EventBody>
                <WithShape color={ev.color} />
                <div>{ev.name}</div>
              </EventBody>
            </EventButton>
          </EventContainer>
        ))}
        {invalidEventReason && (
          <FormHelperText error={true}>{invalidEventReason}</FormHelperText>
        )}

        <Typography variant="caption">
          {t(
            'To ask participants to book multiple events, please create multiple sub-tasks.'
          )}
        </Typography>
      </FormRowGroup>

      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={tokens}
        onChange={(nextHooks) => {
          onChange({
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          });
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsSubTaskCalendlyInviteReadOnly = ({
  value,
  showOnCompleteHookSettings,
  ...props
}: {
  value: ISubTaskCalendlyInviteTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  return (
    <>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          value={value.hooks.onComplete}
          {...props}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};
